/** @jsxImportSource react */
import {
  Box,
  Grid,
  Typography,
  Paper,
  Icon,
  Switch,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  alpha,
} from "@mui/material";
import { StepProps } from "./types";
import { features } from "./constants";
import { StyledTextField } from "./StyledComponents";

export const SubscriptionStep: React.FC<StepProps> = ({ formik }) => {
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Subscription
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Choose a plan and customize features for the business
      </Typography>

      {/* Plan Selection */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ display: "flex", gap: 3, mb: 4 }}>
            {[
              {
                id: "basic",
                title: "Basic",
                icon: "star_border",
                description:
                  "Perfect for small businesses just getting started",
                features: [
                  "Up to 5 users",
                  "Basic inventory",
                  "Single location",
                ],
                colorName: "success" as const,
              },
              {
                id: "professional",
                title: "Professional",
                icon: "star_half",
                description:
                  "Ideal for growing businesses with multiple locations",
                features: [
                  "Up to 20 users",
                  "Advanced inventory",
                  "Multiple locations",
                  "Basic analytics",
                ],
                colorName: "primary" as const,
                recommended: true,
              },
              {
                id: "enterprise",
                title: "Enterprise",
                icon: "star",
                description: "Best for large businesses with complex needs",
                features: [
                  "Unlimited users",
                  "Advanced analytics",
                  "Multiple locations",
                  "All features included",
                ],
                colorName: "secondary" as const,
              },
            ].map((plan) => (
              <Paper
                key={plan.id}
                sx={{
                  flex: 1,
                  p: 3,
                  cursor: "pointer",
                  border: 2,
                  borderColor:
                    formik.values.subscription.plan === plan.id
                      ? `${plan.colorName}.main`
                      : "divider",
                  bgcolor:
                    formik.values.subscription.plan === plan.id
                      ? `${plan.colorName}.lighter`
                      : "background.paper",
                  position: "relative",
                  overflow: "hidden",
                  transition: "all 0.2s ease-in-out",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  minHeight: 280,
                  "&:hover": {
                    borderColor: `${plan.colorName}.main`,
                    transform: "translateY(-4px)",
                    boxShadow: 4,
                  },
                }}
                onClick={() =>
                  formik.setFieldValue("subscription.plan", plan.id)
                }
              >
                {plan.recommended && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 20,
                      right: -35,
                      transform: "rotate(45deg)",
                      bgcolor: "primary.main",
                      color: "primary.contrastText",
                      px: 5,
                      py: 0.5,
                      fontSize: "0.55rem",
                      fontWeight: "bold",
                      boxShadow: 1,
                      zIndex: 1,
                      width: 140,
                      textAlign: "center",
                    }}
                  >
                    RECOMMENDED
                  </Box>
                )}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Icon
                    color={
                      formik.values.subscription.plan === plan.id
                        ? plan.colorName
                        : "action"
                    }
                    fontSize="large"
                  >
                    {plan.icon}
                  </Icon>
                  <Typography
                    variant="h6"
                    color={
                      formik.values.subscription.plan === plan.id
                        ? `${plan.colorName}.main`
                        : "text.primary"
                    }
                  >
                    {plan.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {plan.description}
                </Typography>
                <Box component="ul" sx={{ pl: 2, mt: "auto", mb: 0 }}>
                  {plan.features.map((feature, index) => (
                    <Box
                      component="li"
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        color: "text.secondary",
                        mb: 1,
                        "&::marker": {
                          color: `${plan.colorName}.main`,
                        },
                      }}
                    >
                      <Typography variant="body2">{feature}</Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            ))}
          </Box>
        </Grid>

        {/* Resource Limits */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <Icon color="primary">settings</Icon>
              Resource Limits
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    p: 2,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    "&:hover": { borderColor: "primary.main" },
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <Icon fontSize="small">group</Icon>
                    Users
                  </Typography>
                  <StyledTextField
                    fullWidth
                    type="number"
                    name="subscription.features.max_users"
                    value={formik.values.subscription.features.max_users}
                    onChange={formik.handleChange}
                    InputProps={{
                      inputProps: { min: 1 },
                    }}
                    size="small"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    p: 2,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    "&:hover": { borderColor: "primary.main" },
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <Icon fontSize="small">inventory_2</Icon>
                    Products
                  </Typography>
                  <StyledTextField
                    fullWidth
                    type="number"
                    name="subscription.features.max_products"
                    value={formik.values.subscription.features.max_products}
                    onChange={formik.handleChange}
                    InputProps={{
                      inputProps: { min: 1 },
                    }}
                    size="small"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    p: 2,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    "&:hover": { borderColor: "primary.main" },
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <Icon fontSize="small">location_on</Icon>
                    Locations
                  </Typography>
                  <StyledTextField
                    fullWidth
                    type="number"
                    name="subscription.features.max_locations"
                    value={formik.values.subscription.features.max_locations}
                    onChange={formik.handleChange}
                    InputProps={{
                      inputProps: { min: 1 },
                    }}
                    size="small"
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Additional Features */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Icon color="primary">add_circle</Icon>
                Additional Features
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Enhance your business capabilities with these powerful features
              </Typography>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                gap: 3,
              }}
            >
              {features.map((feature) => (
                <Paper
                  key={feature.id}
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    bgcolor: (theme) =>
                      formik.values.subscription.features[feature.id]
                        ? alpha(theme.palette.primary.main, 0.04)
                        : "background.default",
                    border: "2px solid",
                    borderColor: (theme) =>
                      formik.values.subscription.features[feature.id]
                        ? "primary.main"
                        : "divider",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    position: "relative",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    "&:hover": {
                      borderColor: "primary.main",
                      transform: "translateY(-4px)",
                      boxShadow: (theme) =>
                        `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
                    },
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      bgcolor: "primary.main",
                      opacity: formik.values.subscription.features[feature.id]
                        ? 1
                        : 0,
                      transition: "opacity 0.3s ease",
                    },
                  }}
                  onClick={() =>
                    formik.setFieldValue(
                      `subscription.features.${feature.id}`,
                      !formik.values.subscription.features[feature.id]
                    )
                  }
                >
                  <Box sx={{ position: "relative" }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: (theme) =>
                            formik.values.subscription.features[feature.id]
                              ? "primary.main"
                              : alpha(theme.palette.action.active, 0.04),
                          color: formik.values.subscription.features[feature.id]
                            ? "primary.contrastText"
                            : "action.active",
                          transition: "all 0.3s ease",
                        }}
                      >
                        <Icon sx={{ fontSize: 28 }}>{feature.icon}</Icon>
                      </Box>
                      <Switch
                        checked={
                          formik.values.subscription.features[feature.id]
                        }
                        onChange={formik.handleChange}
                        name={`subscription.features.${feature.id}`}
                        color="primary"
                      />
                    </Box>
                    <Typography variant="h6" gutterBottom>
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {feature.description}
                    </Typography>
                    <Box
                      sx={{
                        mt: "auto",
                        pt: 2,
                        borderTop: 1,
                        borderColor: "divider",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        Key benefits:
                      </Typography>
                      <List dense disablePadding>
                        {feature.benefits.map((benefit, index) => (
                          <ListItem
                            key={index}
                            disableGutters
                            sx={{
                              py: 0.5,
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <Icon
                                fontSize="small"
                                color={
                                  formik.values.subscription.features[
                                    feature.id
                                  ]
                                    ? "primary"
                                    : "action"
                                }
                              >
                                check_circle
                              </Icon>
                            </ListItemIcon>
                            <ListItemText
                              primary={benefit}
                              primaryTypographyProps={{
                                variant: "body2",
                                color: formik.values.subscription.features[
                                  feature.id
                                ]
                                  ? "text.primary"
                                  : "text.secondary",
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
